import { Message, QuotedMessage, type InstagramReplyMode } from '../types/Conversation';
import { conversationHasInstagramMessages } from './whatsappComposerUtils';
import type { Conversation } from '../types/Conversation';
import {
  isMessengerCommentMessage,
  isMessengerPublicReplyMessage,
  resolveConversationHasMessengerComments,
} from './messengerComposerUtils';

export type InstagramCommentSurface = 'comment' | 'live_comment';

export type { InstagramReplyMode };

export interface InstagramComposerContext {
  replyMode: InstagramReplyMode | null;
  pendingCommentId?: string;
  pendingCommentMessageId?: string;
  pendingCommentSurface?: InstagramCommentSurface;
  availableReplyModes: InstagramReplyMode[];
  unrepliedCommentOptions: InstagramPendingCommentOption[];
}

export interface InstagramPendingCommentOption {
  messageId: string;
  commentId: string;
  surface: InstagramCommentSurface;
  commentText: string;
  postLabelKey: string;
  postCaption?: string;
  thumbnailUrl?: string;
  timestamp: string;
}

export const isInstagramCommentMessage = (message: Message): boolean => {
  const surface = message.channelContext?.surface;
  if (surface === 'comment' || surface === 'live_comment') {
    return message.source === 'instagram';
  }

  return Boolean(message.channelContext?.commentId) && message.source === 'instagram';
};

export const conversationHasInstagramComments = (messages: Message[]): boolean =>
  messages.some(isInstagramCommentMessage);

export const resolveConversationHasInstagramComments = (
  conversation: Pick<Conversation, 'containsInstagramComments' | 'messages'>,
): boolean => Boolean(conversation.containsInstagramComments) || conversationHasInstagramComments(conversation.messages);

export const getInstagramCommentPreviewPrefix = (
  message: Message,
  labelForKey: (key: string) => string,
): string | null => {
  const labelKey = getInstagramCommentSurfaceLabelKey(message);
  if (!labelKey) {
    return null;
  }

  return `[${labelForKey(labelKey)}]`;
};

export const getInstagramCommentSurfaceLabelKey = (message: Message): string | null => {
  if (!isInstagramCommentMessage(message)) {
    return null;
  }

  return message.channelContext?.surface === 'live_comment'
    ? 'instagramLiveCommentBadge'
    : 'instagramCommentBadge';
};

const resolveCommentSurface = (message: Message): InstagramCommentSurface =>
  message.channelContext?.surface === 'live_comment' ? 'live_comment' : 'comment';

export const resolveInstagramCommentKey = (message: Message): string | undefined => {
  const commentId = message.channelContext?.commentId?.trim();
  if (commentId) {
    return commentId;
  }

  if (isInstagramCommentMessage(message)) {
    return message.id;
  }

  return undefined;
};


export const isInstagramCommentReplyTarget = (message: Message): boolean => {
  if (!isInstagramCommentMessage(message) || message.instagramCommentDeleted) {
    return false;
  }

  return true;
};

/** @deprecated Use isInstagramCommentReplyTarget */
export const isInstagramCommentUnreplied = (messages: Message[], message: Message): boolean =>
  isInstagramCommentReplyTarget(message);

export const getInstagramPostLabelKey = (message: Message): string => {
  const preview = message.contentPreview;
  const productType = preview?.mediaProductType?.trim().toUpperCase();
  if (productType === 'REELS' || productType === 'REEL') {
    return 'instagramContentPreviewReel';
  }

  if (productType === 'STORY') {
    return 'instagramContentPreviewStory';
  }

  if (preview?.contentType?.trim().toUpperCase() === 'VIDEO') {
    return 'instagramContentPreviewVideo';
  }

  return 'instagramContentPreviewPost';
};

export const listInstagramCommentTargetMessages = (messages: Message[]): Message[] => {
  const comments: Message[] = [];
  const seenCommentIds = new Set<string>();

  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index];
    const commentId = resolveInstagramCommentKey(message);
    if (!commentId || !isInstagramCommentReplyTarget(message) || seenCommentIds.has(commentId)) {
      continue;
    }

    seenCommentIds.add(commentId);
    comments.push(message);
  }

  return comments;
};

/** @deprecated Use listInstagramCommentTargetMessages */
export const listUnrepliedInstagramCommentMessages = listInstagramCommentTargetMessages;

export const buildInstagramPendingCommentOption = (message: Message): InstagramPendingCommentOption => ({
  messageId: message.id,
  commentId: resolveInstagramCommentKey(message) ?? message.id,
  surface: resolveCommentSurface(message),
  commentText: message.content.trim(),
  postLabelKey: getInstagramPostLabelKey(message),
  postCaption: message.contentPreview?.caption?.trim() || undefined,
  thumbnailUrl: message.contentPreview?.thumbnailUrl,
  timestamp: message.timestamp,
});

export const resolveInstagramCommentTargetMessage = (
  messages: Message[],
  options?: {
    quotedMessage?: Message | null;
    selectedCommentMessageId?: string | null;
  },
): Message | null => {
  const selectedCommentMessageId = options?.selectedCommentMessageId;
  if (selectedCommentMessageId) {
    const selected = messages.find(message => message.id === selectedCommentMessageId);
    if (selected && isInstagramCommentReplyTarget(selected)) {
      return selected;
    }

    return null;
  }

  return null;
};

export const resolveInstagramCommentQuotedMessageId = (
  replyMode: InstagramReplyMode | null,
  targetMessage: Message | null,
  quotedMessage?: Message | null,
): string | undefined => {
  if (instagramCommentReplyBlocksAttachments(replyMode) && targetMessage) {
    return targetMessage.id;
  }

  return quotedMessage?.id;
};

export const resolveInstagramComposerContext = (
  messages: Message[],
  quotedMessage?: Message | null,
  tenantDefaultReplyMode: 'public_comment' | 'private_comment' = 'public_comment',
  hasInstagramComments = false,
  selectedCommentMessageId?: string | null,
): InstagramComposerContext => {
  const commentReplyOptions = listInstagramCommentTargetMessages(messages)
    .map(buildInstagramPendingCommentOption);

  if (!conversationHasInstagramMessages(messages) && !hasInstagramComments) {
    return { replyMode: null, availableReplyModes: [], unrepliedCommentOptions: [] };
  }

  if (!selectedCommentMessageId) {
    return {
      replyMode: 'dm',
      availableReplyModes: ['dm'],
      unrepliedCommentOptions: commentReplyOptions,
    };
  }

  const targetMessage = resolveInstagramCommentTargetMessage(messages, {
    quotedMessage,
    selectedCommentMessageId,
  });
  const pendingComment = targetMessage
    ? {
      commentId: resolveInstagramCommentKey(targetMessage) ?? targetMessage.id,
      surface: resolveCommentSurface(targetMessage),
      messageId: targetMessage.id,
    }
    : null;

  if (!pendingComment) {
    return {
      replyMode: 'dm',
      availableReplyModes: ['dm'],
      unrepliedCommentOptions: commentReplyOptions,
    };
  }

  const availableReplyModes: InstagramReplyMode[] = pendingComment.surface === 'live_comment'
    ? ['private_comment']
    : ['private_comment', 'public_comment'];

  const defaultReplyMode: InstagramReplyMode = pendingComment.surface === 'live_comment'
    ? 'private_comment'
    : tenantDefaultReplyMode;

  return {
    replyMode: defaultReplyMode,
    pendingCommentId: pendingComment.commentId,
    pendingCommentMessageId: pendingComment.messageId,
    pendingCommentSurface: pendingComment.surface,
    availableReplyModes,
    unrepliedCommentOptions: commentReplyOptions,
  };
};

export const instagramCommentReplyBlocksAttachments = (
  mode: InstagramReplyMode | null,
): boolean => mode === 'private_comment' || mode === 'public_comment';

/** @deprecated Use instagramCommentReplyBlocksAttachments */
export const instagramPrivateReplyBlocksAttachments = (
  context: InstagramComposerContext,
): boolean => instagramCommentReplyBlocksAttachments(context.replyMode);

export const instagramComposerShowsReplyTarget = (
  selectedCommentMessageId: string | null | undefined,
  replyMode: InstagramReplyMode | null,
  context: InstagramComposerContext,
): boolean => {
  if (!selectedCommentMessageId) {
    return false;
  }

  if (replyMode !== 'public_comment' && replyMode !== 'private_comment') {
    return false;
  }

  return context.pendingCommentMessageId === selectedCommentMessageId;
};

export const getInstagramReplyModeLabelKey = (mode: InstagramReplyMode): string => {
  if (mode === 'public_comment') {
    return 'instagramReplyMode_public_comment';
  }

  if (mode === 'private_comment') {
    return 'instagramReplyMode_private_comment';
  }

  return 'instagramReplyMode_dm';
};

export const messageToQuotedMessage = (message: Message): QuotedMessage => ({
  id: message.id,
  sender: message.sender,
  content: message.content,
  messageType: message.messageType,
  fileName: message.fileName,
  mediaUrl: message.mediaUrl,
});

export const findInstagramReplyMessageForComment = (
  messages: Message[],
  commentMessage: Message,
): { message: Message; replyMode: 'public_comment' | 'private_comment' } | null => {
  const commentKey = resolveInstagramCommentKey(commentMessage);
  if (!commentKey) {
    return null;
  }

  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const candidate = messages[index];
    if (candidate.instagramPublicReplyCommentId === commentKey) {
      return { message: candidate, replyMode: 'public_comment' };
    }

    if (candidate.instagramPrivateReplyCommentId === commentKey) {
      return { message: candidate, replyMode: 'private_comment' };
    }
  }

  return null;
};

export const isInstagramPublicReplyMessage = (message: Message, messages: Message[] = []): boolean => {
  const replyMode = message.instagramReplyMode?.trim().toLowerCase();
  if (replyMode === 'private_comment' || replyMode === 'dm') {
    return false;
  }

  if (replyMode === 'public_comment') {
    return true;
  }

  if (message.instagramPrivateReplyCommentId?.trim()) {
    return false;
  }

  if (message.instagramPublicReplyCommentId?.trim()) {
    return true;
  }

  for (const comment of messages) {
    if (!isInstagramCommentMessage(comment)) {
      continue;
    }

    const reply = findInstagramReplyMessageForComment(messages, comment);
    if (reply?.message.id === message.id && reply.replyMode === 'public_comment') {
      return true;
    }
  }

  return false;
};

/** Inbound comments and outbound public replies (agent/AI) hidden when "Show comments" is off. */
export const isInstagramCommentVisibilityFilteredMessage = (
  message: Message,
  messages: Message[] = [],
): boolean => isInstagramCommentMessage(message) || isInstagramPublicReplyMessage(message, messages);

export const isCommentVisibilityFilteredMessage = (
  message: Message,
  messages: Message[] = [],
): boolean => isInstagramCommentVisibilityFilteredMessage(message, messages)
  || isMessengerCommentMessage(message)
  || isMessengerPublicReplyMessage(message, messages);

export const resolveConversationHasCommentThreads = (
  conversation: Pick<Conversation, 'containsInstagramComments' | 'containsMessengerComments' | 'messages'>,
): boolean => resolveConversationHasInstagramComments(conversation)
  || resolveConversationHasMessengerComments(conversation);

export const filterMessagesForCommentVisibility = (
  messages: Message[],
  showCommentThreads: boolean,
): Message[] => {
  if (showCommentThreads) {
    return messages;
  }

  return messages.filter(message => !isCommentVisibilityFilteredMessage(message, messages));
};

export const conversationHasVisibleNonCommentMessages = (messages: Message[]): boolean =>
  messages.some(message => !isCommentVisibilityFilteredMessage(message, messages));

export const shouldHideConversationWhenCommentsFiltered = (
  conversation: Pick<Conversation, 'containsInstagramComments' | 'containsMessengerComments' | 'messages' | 'hasMoreMessages'>,
  showCommentThreads: boolean,
): boolean => {
  if (showCommentThreads) {
    return false;
  }

  if (!resolveConversationHasCommentThreads(conversation)) {
    return false;
  }

  if (conversationHasVisibleNonCommentMessages(conversation.messages)) {
    return false;
  }

  if (conversation.hasMoreMessages) {
    return false;
  }

  return true;
};

export const getInstagramComposerPlaceholderKey = (
  mode: InstagramReplyMode | null,
  options: {
    isInteractiveComposerOpen: boolean;
    attachmentCount: number;
  },
): string => {
  if (options.isInteractiveComposerOpen) {
    return 'interactiveComposerMessagePlaceholder';
  }

  if (options.attachmentCount > 1) {
    return 'addCaptionFirstOptional';
  }

  if (options.attachmentCount === 1) {
    return 'addCaptionOptional';
  }

  if (mode === 'public_comment') {
    return 'instagramComposerPlaceholderPublic';
  }

  if (mode === 'private_comment') {
    return 'instagramComposerPlaceholderPrivate';
  }

  if (mode === 'dm') {
    return 'instagramComposerPlaceholderDm';
  }

  return 'typeMessageOrAskAi';
};

